import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaidFees, generateReceipt } from "../../redux/admin/fees.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";
import { downloadAdminReceipt } from "../../api/admin/fees.api";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =====================================================
   MNC EXECUTIVE DIGITAL RECEIPT PREVIEW MODAL
===================================================== */
const ReceiptModal = ({ open, record, onClose, onDownload, isGenerating }) => {
  const printRef = useRef();

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open || !record) return null;

  const baseFee = parseFloat(record.base_fee || 0);
  const discount = parseFloat(record.discount || 0);
  const netFee = parseFloat(record.net_fee || baseFee - discount);
  const paidAmount = parseFloat(record.amount || record.paid_amount || 0);
  const balanceDue = parseFloat(record.balance_due || 0);
  
  const breakdownList = record.advance_breakdown || [];
  const receiptDisplayNumber = record.receipt_number || `AAD-FEE-${record.target_year || new Date().getFullYear()}-` + String(record.id).padStart(6, '0');

  const months = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const targetMonthLabel = months[Number(record.target_month || record.bill_month)] || "Period";

  return (
    <div className="mnc-receipt-overlay" onMouseDown={onClose}>
      <div className="mnc-receipt-modal animate-scale" onMouseDown={(e) => e.stopPropagation()}>
        
        {/* MODAL HEADER */}
        <div className="mnc-receipt-header">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="mnc-receipt-chip">Official Fee Voucher</span>
            <span className="receipt-number-badge">#{receiptDisplayNumber}</span>
          </div>
          <button className="btn-close-modal" onClick={onClose}>&times;</button>
        </div>

        {/* RECEIPT CARD CONTENT */}
        <div className="mnc-receipt-body" ref={printRef}>
          <div className="receipt-brand-row">
            <div>
              <h2 className="receipt-org-title">AADHAV ACADEMY</h2>
              <p className="receipt-org-sub">Fee Settlement &amp; Ledger Audit Voucher</p>
            </div>
            <div className="text-end mt-2 mt-sm-0">
              <span className={`receipt-status-stamp ${balanceDue <= 0 ? "is-paid" : "is-partial"}`}>
                {balanceDue <= 0 ? "PAID & CLEARED" : "PARTIAL SETTLEMENT"}
              </span>
            </div>
          </div>

          <hr className="receipt-divider" />

          {/* STUDENT & TRANSACTION METADATA */}
          <div className="receipt-meta-grid">
            <div className="meta-block">
              <span className="meta-label">Student Name</span>
              <strong className="meta-value">{record.name}</strong>
            </div>
            <div className="meta-block">
              <span className="meta-label">Admission Number</span>
              <strong className="meta-value">{record.admission_number || record.student_id}</strong>
            </div>
            <div className="meta-block">
              <span className="meta-label">Billing Period</span>
              <strong className="meta-value">{targetMonthLabel} {record.target_year || record.bill_year}</strong>
            </div>
            <div className="meta-block">
              <span className="meta-label">Transaction Date</span>
              <strong className="meta-value">{record.payment_date}</strong>
            </div>
          </div>

          {/* BREAKDOWN TABLE */}
          <div className="receipt-calc-table">
            <div className="calc-row">
              <span>Base Monthly Fee</span>
              <strong>₹{baseFee.toFixed(2)}</strong>
            </div>
            {discount > 0 && (
              <div className="calc-row text-danger">
                <span>Scholarship / Discount</span>
                <strong>−₹{discount.toFixed(2)}</strong>
              </div>
            )}
            <div className="calc-row calc-row-subtotal">
              <span>Net Payable Fee</span>
              <strong>₹{netFee.toFixed(2)}</strong>
            </div>

            {/* DYNAMIC CHILD BREAKDOWN RENDERER */}
            {breakdownList.length > 0 ? (
              breakdownList.map((item, idx) => {
                const itemAmt = parseFloat(item.amount || 0);
                const pType = item.payment_type;
                
                let label = `Payment (${item.payment_mode?.toUpperCase() || 'CASH'})`;
                let badgeClass = "text-success";

                if (pType === 'advance') {
                  label = `Advance Deposit Received (${item.payment_mode?.toUpperCase() || 'CASH'})`;
                  badgeClass = "text-info";
                } else if (pType === 'advance_adjustment') {
                  const advDate = item.parent_advance_date ? new Date(item.parent_advance_date).toLocaleDateString('en-GB') : 'Earlier';
                  label = `Advance Adjusted (From Deposit on ${advDate})`;
                  badgeClass = "text-purple fw-bold";
                }

                return (
                  <div key={idx} className="calc-row">
                    <span className="text-muted fs-8">↳ {label}</span>
                    <strong className={badgeClass}>₹{itemAmt.toFixed(2)}</strong>
                  </div>
                );
              })
            ) : (
              <div className="calc-row calc-row-highlight">
                <span>Amount Paid Today ({record.payment_mode?.toUpperCase() || "CASH"})</span>
                <strong className="text-success">₹{paidAmount.toFixed(2)}</strong>
              </div>
            )}

            <div className="calc-row calc-row-highlight mt-1">
              <span>Total Settled for Period</span>
              <strong className="text-success">₹{paidAmount.toFixed(2)}</strong>
            </div>

            <div className="calc-row">
              <span>Remaining Balance Due</span>
              <strong className={balanceDue > 0 ? "text-danger" : "text-dark"}>₹{balanceDue.toFixed(2)}</strong>
            </div>
          </div>

          {/* FOOTER AUDIT NOTES */}
          <div className="receipt-footer-notes">
            <p><strong>Payment Channel:</strong> {(record.payment_mode || "CASH").toUpperCase()} · <strong>Voucher Ref:</strong> #{record.id}</p>
            {record.remarks && <p><strong>Notes:</strong> {record.remarks}</p>}
            <span className="receipt-timestamp">System Logged: {record.created_at || new Date().toLocaleString()}</span>
          </div>
        </div>

        {/* MODAL ACTIONS - DOWNLOAD ONLY */}
        <div className="mnc-receipt-actions">
          <button
            type="button"
            className="btn-mnc-primary w-100 justify-content-center"
            onClick={() => onDownload(record.payment_id || record.id)}
            disabled={isGenerating}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="me-1">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download PDF Receipt
          </button>
        </div>

      </div>
    </div>
  );
};

/* =====================================================
   REFINED EXECUTIVE CONFIRMATION MODAL WITH ADVANCE BREAKDOWN
===================================================== */
const ConfirmationModal = ({ open, record, onClose, onConfirm, isGenerating }) => {
  if (!open || !record) return null;

  const months = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const targetMonthLabel = months[Number(record.target_month || record.bill_month)] || "Period";
  const paidAmount = parseFloat(record.amount || record.paid_amount || 0);
  const breakdownList = record.advance_breakdown || [];

  return (
    <div className="mnc-confirm-overlay" onMouseDown={onClose}>
      <div className="mnc-confirm-dialog animate-scale" onMouseDown={(e) => e.stopPropagation()}>
        
        {/* ICON & HEADER */}
        <div className="confirm-header-content">
          <div className="confirm-icon-badge">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div>
            <h3 className="confirm-title">Generate Digital Receipt</h3>
            <p className="confirm-subtitle">Verify payment ledger &amp; advance breakdown before issuing official voucher</p>
          </div>
        </div>

        {/* STUDENT & BILLING SUMMARY CARD */}
        <div className="confirm-details-card">
          <div className="confirm-row">
            <span className="c-label">Student Name</span>
            <strong className="c-val">{record.name}</strong>
          </div>
          <div className="confirm-row">
            <span className="c-label">Admission Number</span>
            <strong className="c-val">{record.admission_number || record.student_id}</strong>
          </div>
          <div className="confirm-row">
            <span className="c-label">Billing Period</span>
            <strong className="c-val text-primary">{targetMonthLabel} {record.target_year || record.bill_year}</strong>
          </div>

          <div className="confirm-divider my-2 border-top pt-2 mt-1"></div>

          {/* DETAILED LEDGER BREAKDOWN LIST */}
          <div className="confirm-sub-header mb-1">
            <span className="fs-7 text-uppercase font-weight-bold text-muted">Transaction &amp; Advance Breakdown:</span>
          </div>

          {breakdownList.length > 0 ? (
            breakdownList.map((item, idx) => {
              const itemAmt = parseFloat(item.amount || 0);
              const pType = item.payment_type;
              
              let label = `Payment (${item.payment_mode?.toUpperCase() || 'CASH'})`;
              let valClass = "text-success";

              if (pType === 'advance') {
                label = `Unconsumed Advance Deposit (${item.payment_mode?.toUpperCase() || 'CASH'})`;
                valClass = "text-info";
              } else if (pType === 'advance_adjustment') {
                label = `Advance Credit Adjustment`;
                valClass = "text-purple";
              }

              return (
                <div key={idx} className="confirm-row ps-2 border-start border-2 border-primary my-1">
                  <span className="c-label fs-8">↳ {label}</span>
                  <strong className={`c-val ${valClass}`}>₹{itemAmt.toFixed(2)}</strong>
                </div>
              );
            })
          ) : (
            <div className="confirm-row">
              <span className="c-label">Collected Amount</span>
              <strong className="c-val text-success">₹{paidAmount.toFixed(2)} ({record.payment_mode?.toUpperCase() || "CASH"})</strong>
            </div>
          )}

          <div className="confirm-row border-top pt-2 mt-1">
            <span className="c-label font-weight-bold">Total Period Settlement</span>
            <strong className="c-val text-success font-weight-bold">₹{paidAmount.toFixed(2)}</strong>
          </div>
        </div>

        <div className="confirm-notice-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-info flex-shrink-0 mt-0.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>By clicking confirm, you verify that all tuition and uncleared advance credits for this cycle are fully reconciled.</span>
        </div>

        {/* ACTION BUTTONS */}
        <div className="confirm-actions">
          <button type="button" className="btn-confirm-cancel" onClick={onClose} disabled={isGenerating}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-confirm-action"
            onClick={onConfirm}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Generating...
              </>
            ) : (
              "Confirm & Generate"
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

/* =====================================================
   SEARCHABLE STUDENT COMBOBOX FOR FILTERS
===================================================== */
const StudentFilterCombobox = ({ students, value, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(value)),
    [students, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        String(s.admission_number || "").toLowerCase().includes(q)
    );
  }, [students, query]);

  useEffect(() => {
    if (!open) {
      setQuery(selectedStudent ? `${selectedStudent.admission_number} — ${selectedStudent.name}` : "");
    }
  }, [selectedStudent, open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const commit = (student) => {
    onSelect(student ? String(student.id) : "");
    setOpen(false);
  };

  return (
    <div className="mnc-combobox" ref={wrapperRef}>
      <div className={`mnc-combobox-field ${open ? "is-open" : ""}`}>
        <input
          type="text"
          className="form-control-mnc"
          placeholder="Filter student..."
          value={query}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => setQuery(e.target.value)}
        />
        {value && !open && (
          <button
            type="button"
            className="mnc-combobox-clear"
            onClick={() => commit(null)}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="mnc-combobox-list" role="listbox">
          <li
            className="mnc-combobox-option text-muted fw-bold"
            onMouseDown={(e) => {
              e.preventDefault();
              commit(null);
            }}
          >
            All Students
          </li>
          {filtered.map((student) => (
            <li
              key={student.id}
              role="option"
              className={`mnc-combobox-option ${String(student.id) === String(value) ? "is-selected" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(student);
              }}
            >
              <span className="mnc-combobox-option-name">{student.name}</span>
              <span className="mnc-combobox-option-adm">{student.admission_number}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* =====================================================
   MAIN REPORT & TERMINAL COMPONENT
===================================================== */
const PaidFees = () => {
  const dispatch = useDispatch();

  const { students = [] } = useSelector((state) => state.admin?.students || {});
  const {
    paidFees = [],
    paidFeesCurrentPage = 1,
    paidFeesTotalPages = 1,
    paidFeesTotalRecords = 0,
    summaryTotals = null,
    loading = false,
  } = useSelector((state) => state.admin?.fees || {});

  const pagination = {
    current_page: paidFeesCurrentPage,
    total_pages: paidFeesTotalPages,
    total_records: paidFeesTotalRecords,
  };

  const [processingId, setProcessingId] = useState(null);

  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Modals State
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [activeReceiptRecord, setActiveReceiptRecord] = useState(null);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [activeConfirmRecord, setActiveConfirmRecord] = useState(null);

  const months = [
    { value: "", label: "All Months" },
    { value: "1", label: "January" }, { value: "2", label: "February" }, { value: "3", label: "March" },
    { value: "4", label: "April" }, { value: "5", label: "May" }, { value: "6", label: "June" },
    { value: "7", label: "July" }, { value: "8", label: "August" }, { value: "9", label: "September" },
    { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
  ];

  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
  }, [dispatch]);

  const loadData = useCallback(
    (page = 1) => {
      dispatch(
        fetchPaidFees({
          page,
          limit: 15,
          student_id: selectedStudentId,
          month: selectedMonth,
          year: selectedYear,
          status: selectedStatus,
        })
      );
    },
    [dispatch, selectedStudentId, selectedMonth, selectedYear, selectedStatus]
  );

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  const filteredRecords = useMemo(() => {
    return paidFees.filter((fee) => {
      if (selectedStudentId) {
        if (String(fee.student_id) !== String(selectedStudentId)) return false;
      }
      if (selectedMonth) {
        const feeMonth = Number(fee.target_month || fee.bill_month || fee.month);
        if (feeMonth !== Number(selectedMonth)) return false;
      }
      if (selectedYear) {
        const feeYear = Number(fee.target_year || fee.bill_year || fee.year);
        if (feeYear !== Number(selectedYear)) return false;
      }
      if (selectedStatus !== "all") {
        if (selectedStatus === "advance") {
          if (fee.payment_type !== "advance") return false;
        } else if (selectedStatus === "partially_paid") {
          if (fee.bill_status !== "partially_paid" && fee.status !== "partially_paid") return false;
        } else if (selectedStatus === "unpaid") {
          if (fee.bill_status !== "unpaid" && fee.status !== "unpaid") return false;
        } else if (selectedStatus === "paid") {
          if (fee.payment_type === "advance") return false;
          if (fee.bill_status !== "paid" && fee.status !== "paid") return false;
        }
      }
      return true;
    });
  }, [paidFees, selectedStudentId, selectedMonth, selectedYear, selectedStatus]);

const summaryMetrics = useMemo(() => {
    let directTuition = 0;
    let advanceReceived = 0;
    let advanceAdjusted = 0;
    let totalBalanceDue = 0;

    filteredRecords.forEach((item) => {
      const amt = parseFloat(item.amount || item.paid_amount || 0);
      const due = parseFloat(item.balance_due || 0);

      if (item.payment_type === "advance") {
        advanceReceived += amt;
      } else if (item.payment_type === "advance_adjustment" || item.payment_type === "mixed_tuition_advance") {
        advanceAdjusted += amt;
      } else {
        directTuition += amt;
      }
      totalBalanceDue += due;
    });

    const apiGross = summaryTotals?.gross_collections;
    const apiAdvance = summaryTotals?.unconsumed_advance;
    const apiDues = summaryTotals?.total_outstanding_due;

    const finalAdvance = apiAdvance !== undefined && apiAdvance !== null ? parseFloat(apiAdvance) : advanceReceived;
    const finalGross = apiGross !== undefined && apiGross !== null ? parseFloat(apiGross) : (directTuition + advanceAdjusted);
    const finalDues = apiDues !== undefined && apiDues !== null ? parseFloat(apiDues) : totalBalanceDue;

    return {
      grossCollections: finalGross,
      directTuitionPaid: directTuition,
      advanceReceived: finalAdvance,
      advanceAdjusted: advanceAdjusted,
      totalPendingDue: finalDues,
      recordCount: filteredRecords.length,
    };
  }, [filteredRecords, summaryTotals]);

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    loadData(page);
  };

  const handleOpenReceiptModal = (record) => {
    setActiveReceiptRecord(record);
    setReceiptModalOpen(true);
  };

  const handleOpenConfirmModal = (record) => {
    setActiveConfirmRecord(record);
    setConfirmModalOpen(true);
  };

  const handleExecuteGenerateReceipt = async () => {
    if (!activeConfirmRecord) return;
    const paymentId = activeConfirmRecord.payment_id || activeConfirmRecord.id;

    try {
      setProcessingId(paymentId);
      await dispatch(generateReceipt(paymentId)).unwrap();
      toastr.success("Receipt generated successfully");
      setConfirmModalOpen(false);
      loadData(pagination.current_page);
      handleOpenReceiptModal(activeConfirmRecord);
    } catch (error) {
      toastr.error(error || "Failed to generate receipt");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDownloadReceipt = async (id) => {
    try {
      await downloadAdminReceipt(id);
      toastr.success("Download started");
    } catch (error) {
      toastr.error("Download failed");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <div className="precise-app-bg">
      <div className="precise-card">

        {/* HEADER */}
        <header className="precise-header">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <span className="meta-tag">Financial Reports &amp; Ledger Audit</span>
              <h1>Fee Settlement &amp; Payment Ledger</h1>
              <p>Comprehensive monthly collections, advance adjustments, and digital receipt generation</p>
            </div>
          </div>
        </header>

        {/* FINANCIAL SUMMARY CARDS */}
        <section className="summary-cards-grid p-3 p-md-4 border-bottom bg-light">
          <div className="row g-3">
            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card-metric border-left-primary">
                <span className="metric-label">Total Collections</span>
                <h3 className="metric-value text-primary">{formatCurrency(summaryMetrics.grossCollections)}</h3>
                <span className="metric-sub">Cumulative Influx</span>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card-metric border-left-success">
                <span className="metric-label">Direct Tuition Influx</span>
                <h3 className="metric-value text-success">{formatCurrency(summaryMetrics.directTuitionPaid)}</h3>
                <span className="metric-sub">Paid via UPI / Cash</span>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card-metric border-left-info">
                <span className="metric-label">Advance Credit Balance</span>
                <h3 className="metric-value text-info">{formatCurrency(summaryMetrics.advanceReceived)}</h3>
                <span className="metric-sub">Uncleared / Unconsumed</span>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-3">
              <div className="card-metric border-left-danger">
                <span className="metric-label">Pending Dues</span>
                <h3 className="metric-value text-danger">{formatCurrency(summaryMetrics.totalPendingDue)}</h3>
                <span className="metric-sub">Outstanding Balance</span>
              </div>
            </div>
          </div>
        </section>

        {/* TOOLBAR & FILTERS */}
        <section className="precise-toolbar p-3 p-md-4 bg-white border-bottom">
          <div className="row g-2 align-items-center w-100 m-0">

            <div className="col-12 col-md-4 p-1">
              <StudentFilterCombobox
                students={students}
                value={selectedStudentId}
                onSelect={(id) => setSelectedStudentId(id)}
              />
            </div>

            <div className="col-6 col-md-2 p-1">
              <select className="form-select-mnc" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2 p-1">
              <input
                type="number"
                className="form-control-mnc"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                placeholder="Year (e.g. 2026)"
              />
            </div>

            <div className="col-12 col-md-4 p-1">
              <select className="form-select-mnc font-weight-bold" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="all">Status: All</option>
                <option value="paid">Paid</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="advance">Advance</option>
              </select>
            </div>

          </div>
        </section>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="precise-table">
            <thead>
              <tr>
                <th>Student &amp; ID</th>
                <th>Target Period</th>
                <th>Voucher Type &amp; Mode</th>
                <th>Amount Paid</th>
                <th>Master Bill Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="state-msg">
                    <div className="spinner-border spinner-border-sm me-3 text-primary"></div>
                    Loading audit reports...
                  </td>
                </tr>
              ) : filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="6" className="state-msg">
                    No transaction records matching the specified criteria.
                  </td>
                </tr>
              ) : (
                filteredRecords.map((fee) => {
                  const isAdv = fee.payment_type === "advance";
                  const isAdj = fee.payment_type === "advance_adjustment" || fee.payment_type === "mixed_tuition_advance";
                  const billStatus = fee.bill_status || fee.status || "paid";
                  const hasReceipt = fee.has_receipt || fee.receipt_generated;
                  const rowId = fee.payment_id || fee.id;
                  const rowReceiptNumber = fee.receipt_number || `AAD-FEE-${fee.target_year || new Date().getFullYear()}-` + String(rowId).padStart(6, '0');

                  return (
                    <tr key={rowId} className="precise-row">
                      <td>
                        <div className="user-info">
                          <div className="user-icon">{fee.name?.charAt(0) || "S"}</div>
                          <div>
                            <span className="u-name">{fee.name}</span>
                            <span className="u-id">
                              Adm: {fee.admission_number || fee.student_id} {fee.receipt_number ? `· Receipt: #${rowReceiptNumber}` : ""}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="fw-700 text-dark">
                          {months.find((m) => Number(m.value) === Number(fee.target_month || fee.bill_month))?.label || "Period"}{" "}
                          {fee.target_year || fee.bill_year}
                        </span>
                        <span className="u-id">Logged: {fee.payment_date}</span>
                      </td>

                      <td>
                        <span className={`type-badge ${isAdv ? "badge-advance" : isAdj ? "badge-adjustment" : "badge-tuition"}`}>
                          {isAdv ? "ADVANCE DEPOSIT" : isAdj ? "ADVANCE ADJUSTED" : "TUITION PAYMENT"}
                        </span>
                        <span className="pay-mode ms-1">
                          {(fee.payment_mode || "CASH").toUpperCase()}
                        </span>
                      </td>

                      <td>
                        <span className="fee-amount">{formatCurrency(fee.amount || fee.paid_amount)}</span>
                        {parseFloat(fee.balance_due) > 0 && (
                          <span className="u-id text-danger">Pending: {formatCurrency(fee.balance_due)}</span>
                        )}
                      </td>

                      <td>
                        <span className={`status-pill ${billStatus === "paid" ? "status-success" : billStatus === "partially_paid" ? "status-warning" : "status-danger"}`}>
                          {billStatus === "paid" ? "Fully Paid" : billStatus === "partially_paid" ? "Partially Paid" : "Unpaid"}
                        </span>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <div className="d-flex justify-content-end gap-1">
                          {hasReceipt ? (
                            <button
                              className="btn-view-receipt"
                              onClick={() => handleOpenReceiptModal(fee)}
                              title="View Generated Digital Receipt"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="me-1">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              View
                            </button>
                          ) : (
                            <button
                              className="btn-generate-receipt"
                              disabled={processingId === rowId}
                              onClick={() => handleOpenConfirmModal(fee)}
                              title="Generate Digital Receipt"
                            >
                              {processingId === rowId ? "Generating..." : "Generate"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <footer className="precise-footer">
          <div className="stats">
            Showing <b>{filteredRecords.length}</b> of <b>{pagination.total_records}</b> consolidated monthly ledger entries
          </div>
          <div className="pagination-group">
            <button className="btn-pg" disabled={pagination.current_page <= 1 || loading} onClick={() => handlePageChange(pagination.current_page - 1)}>
              Prev
            </button>
            <div className="pg-indicator">
              Page <span>{pagination.current_page}</span> of {pagination.total_pages}
            </div>
            <button className="btn-pg" disabled={pagination.current_page >= pagination.total_pages || loading} onClick={() => handlePageChange(pagination.current_page + 1)}>
              Next
            </button>
          </div>
        </footer>

      </div>

      {/* EXECUTIVE DIGITAL RECEIPT PREVIEW MODAL */}
      <ReceiptModal
        open={receiptModalOpen}
        record={activeReceiptRecord}
        onClose={() => setReceiptModalOpen(false)}
        onDownload={handleDownloadReceipt}
        isGenerating={processingId === (activeReceiptRecord?.payment_id || activeReceiptRecord?.id)}
      />

      {/* REFINED CONFIRMATION MODAL */}
      <ConfirmationModal
        open={confirmModalOpen}
        record={activeConfirmRecord}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleExecuteGenerateReceipt}
        isGenerating={processingId === (activeConfirmRecord?.payment_id || activeConfirmRecord?.id)}
      />

      {/* MNC-STANDARD PROFESSIONAL STYLES WITH MOBILE RESPONSIVE POLISH & PRECISE TYPOGRAPHY */}
      <style>{`
        .precise-app-bg { background: #f8fafc; min-height: 100vh; padding: 1.5rem 0.75rem; font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif; color: #1e293b; }
        .precise-card { max-width: 1200px; margin: 0 auto; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.05); overflow: hidden; }
        .precise-header { padding: 1.25rem 1.5rem; background: #fff; border-bottom: 1px solid #f1f5f9; }
        .precise-header h1 { font-size: 1.2rem; font-weight: 800; margin: 0.3rem 0; color: #0f172a; letter-spacing: -0.02em; }
        .precise-header p { color: #64748b; font-size: 0.8rem; margin: 0; }
        .meta-tag { background: #eff6ff; color: #2563eb; padding: 0.2rem 0.65rem; border-radius: 999px; font-size: 0.65rem; font-weight: 700; text-transform: uppercase; }

        .card-metric { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 1rem; box-shadow: 0 2px 4px rgba(0,0,0,0.02); height: 100%; }
        .border-left-primary { border-left: 4px solid #2563eb; }
        .border-left-success { border-left: 4px solid #10b981; }
        .border-left-info { border-left: 4px solid #0284c7; }
        .border-left-danger { border-left: 4px solid #ef4444; }
        .metric-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; color: #64748b; display: block; margin-bottom: 4px; }
        .metric-value { font-size: 1.15rem; font-weight: 800; margin: 0; letter-spacing: -0.02em; word-break: break-all; }
        .metric-sub { font-size: 0.7rem; color: #94a3b8; font-weight: 600; display: block; margin-top: 4px; }

        .form-select-mnc, .form-control-mnc { width: 100%; padding: 0.55rem 0.8rem; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.82rem; color: #1e293b; background-color: #fff; outline: none; }
        .form-select-mnc:focus, .form-control-mnc:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }

        .mnc-combobox { position: relative; width: 100%; }
        .mnc-combobox-field { position: relative; display: flex; align-items: center; }
        .mnc-combobox-clear { position: absolute; right: 8px; width: 22px; height: 22px; border: none; background: #f1f5f9; color: #64748b; border-radius: 50%; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .mnc-combobox-list { position: absolute; z-index: 40; top: calc(100% + 4px); left: 0; right: 0; max-height: 220px; overflow-y: auto; background: #fff; border: 1px solid #cbd5e1; border-radius: 8px; box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.15); margin: 0; padding: 4px; list-style: none; }
        .mnc-combobox-option { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; cursor: pointer; font-size: 0.8rem; color: #334155; }
        .mnc-combobox-option:hover, .mnc-combobox-option.is-selected { background: #eef4ff; color: #2563eb; font-weight: 600; }
        .mnc-combobox-option-adm { font-size: 0.7rem; color: #94a3b8; }

        .precise-table { width: 100%; border-collapse: collapse; min-width: 750px; }
        .precise-table th { background: #f8fafc; padding: 0.85rem 1.2rem; text-align: left; font-size: 0.68rem; text-transform: uppercase; color: #64748b; font-weight: 700; border-bottom: 1px solid #e2e8f0; }
        .precise-table td { padding: 0.9rem 1.2rem; border-bottom: 1px solid #f1f5f9; font-size: 0.83rem; vertical-align: middle; }
        .precise-row:hover { background: #f8fafc; }

        .user-info { display: flex; align-items: center; gap: 10px; }
        .user-icon { width: 32px; height: 32px; border-radius: 8px; background: #f1f5f9; color: #334155; display: flex; align-items: center; justify-content: center; font-weight: 700; border: 1px solid #e2e8f0; flex-shrink: 0; }
        .u-name { display: block; font-weight: 700; color: #0f172a; }
        .u-id { display: block; font-size: 0.7rem; color: #94a3b8; font-weight: 500; }

        .type-badge { font-size: 0.6rem; font-weight: 800; padding: 2px 6px; border-radius: 4px; text-transform: uppercase; white-space: nowrap; }
        .badge-tuition { background: #e0f2fe; color: #0369a1; }
        .badge-advance { background: #fef3c7; color: #92400e; }
        .badge-adjustment { background: #f3e8ff; color: #6b21a8; }
        .pay-mode { background: #f1f5f9; color: #475569; padding: 2px 5px; border-radius: 4px; font-size: 0.6rem; font-weight: 700; }

        .status-pill { font-size: 0.65rem; font-weight: 700; padding: 3px 8px; border-radius: 999px; white-space: nowrap; }
        .status-success { background: #dcfce7; color: #15803d; }
        .status-warning { background: #fef9c3; color: #a16207; }
        .status-danger { background: #fee2e2; color: #b91c1c; }

        .fee-amount { display: block; color: #059669; font-weight: 800; font-size: 0.88rem; }

        .btn-view-receipt { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.35rem 0.75rem; font-size: 0.72rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; white-space: nowrap; }
        .btn-view-receipt:hover { background: #e2e8f0; color: #0f172a; }
        .btn-generate-receipt { background: #0071e3; color: #fff; border: none; border-radius: 6px; padding: 0.35rem 0.75rem; font-size: 0.72rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; white-space: nowrap; }
        .btn-generate-receipt:hover:not(:disabled) { background: #0058b0; }

        .precise-footer { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 10px; align-items: center; background: #fff; border-top: 1px solid #f1f5f9; text-align: center; }
        .stats { font-size: 0.75rem; color: #64748b; }
        .pagination-group { display: flex; align-items: center; gap: 0.75rem; }
        .btn-pg { border: 1px solid #e2e8f0; background: #fff; color: #475569; padding: 0.35rem 0.75rem; border-radius: 6px; cursor: pointer; font-size: 0.72rem; font-weight: 600; }
        .btn-pg:disabled { opacity: 0.4; cursor: not-allowed; }
        .pg-indicator { font-size: 0.72rem; font-weight: 700; color: #64748b; }
        .pg-indicator span { color: #2563eb; background: #eff6ff; padding: 2px 6px; border-radius: 4px; }
        .state-msg { text-align: center; padding: 3rem !important; color: #94a3b8; font-size: 0.85rem; }

        /* MODALS RESPONSIVE STYLING & PRECISE TYPOGRAPHY */
        .mnc-receipt-overlay, .mnc-confirm-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 12px; }
        .mnc-receipt-modal, .mnc-confirm-dialog { background: #ffffff; border-radius: 16px; max-width: 480px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25); border: 1px solid #e2e8f0; }

        .mnc-receipt-header { padding: 14px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 5; }
        .mnc-receipt-chip { background: #0071e3; color: #ffffff; font-size: 0.62rem; font-weight: 800; padding: 4px 9px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.04em; }
        
        .receipt-number-badge {
          font-family: 'SF Mono', SFProDisplay, Menlo, Monaco, Consolas, monospace;
          font-size: 0.75rem;
          font-weight: 700;
          color: #334155;
          background: #f1f5f9;
          padding: 3px 8px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          letter-spacing: -0.01em;
          display: inline-flex;
          align-items: center;
        }

        .btn-close-modal { background: transparent; border: none; font-size: 1.3rem; color: #64748b; cursor: pointer; line-height: 1; }
        .btn-close-modal:hover { color: #0f172a; }

        .mnc-receipt-body { padding: 18px; }
        .receipt-brand-row { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-start; }
        .receipt-org-title { font-size: 1.05rem; font-weight: 800; color: #0f172a; margin: 0; }
        .receipt-org-sub { font-size: 0.7rem; color: #64748b; margin: 2px 0 0; }
        .receipt-status-stamp { display: inline-block; font-size: 0.6rem; font-weight: 800; padding: 3px 8px; border-radius: 6px; text-transform: uppercase; }
        .receipt-status-stamp.is-paid { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        .receipt-status-stamp.is-partial { background: #fef9c3; color: #a16207; border: 1px solid #fef08a; }

        .receipt-divider { border: 0; border-top: 1px dashed #cbd5e1; margin: 14px 0; }

        .receipt-meta-grid { display: grid; grid-template-columns: 1fr; gap: 10px; background: #f8fafc; border: 1px solid #f1f5f9; padding: 10px 12px; border-radius: 10px; margin-bottom: 14px; }
        .meta-label { display: block; font-size: 0.65rem; text-transform: uppercase; font-weight: 700; color: #64748b; margin-bottom: 2px; }
        .meta-value { display: block; font-size: 0.8rem; color: #0f172a; font-weight: 700; word-break: break-all; }

        .receipt-calc-table { border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-bottom: 14px; }
        .calc-row { display: flex; justify-content: space-between; padding: 8px 12px; font-size: 0.78rem; border-bottom: 1px solid #f1f5f9; color: #334155; gap: 8px; }
        .calc-row:last-child { border-bottom: none; }
        .calc-row-subtotal { background: #f8fafc; font-weight: 700; }
        .calc-row-highlight { background: #f0fdf4; font-weight: 800; font-size: 0.85rem; }
        .text-purple { color: #7c3aed; }

        .receipt-footer-notes { font-size: 0.7rem; color: #64748b; border-top: 1px solid #f1f5f9; padding-top: 8px; }
        .receipt-footer-notes p { margin: 0 0 2px; word-break: break-all; }
        .receipt-timestamp { font-size: 0.62rem; color: #94a3b8; display: block; margin-top: 4px; }

        .mnc-receipt-actions { padding: 14px 18px; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
        .btn-mnc-primary { background: #0071e3; color: #ffffff; border: none; border-radius: 8px; padding: 7px 16px; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; }
        .btn-mnc-primary:hover:not(:disabled) { background: #0058b0; }

        /* CONFIRMATION MODAL */
        .confirm-header-content { display: flex; align-items: flex-start; gap: 12px; padding: 18px 18px 14px; border-bottom: 1px solid #f1f5f9; }
        .confirm-icon-badge { width: 40px; height: 40px; border-radius: 10px; background: #eff6ff; color: #0071e3; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .confirm-title { font-size: 1rem; font-weight: 800; color: #0f172a; margin: 0 0 2px; }
        .confirm-subtitle { font-size: 0.75rem; color: #64748b; margin: 0; }

        .confirm-details-card { margin: 16px 18px 0; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; gap: 6px; }
        .confirm-row { display: flex; justify-content: space-between; font-size: 0.8rem; gap: 6px; }
        .c-label { color: #64748b; font-weight: 600; }
        .c-val { color: #0f172a; font-weight: 700; text-align: right; word-break: break-all; }

        .confirm-notice-box { margin: 14px 18px 0; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 9px 12px; display: flex; gap: 8px; font-size: 0.72rem; color: #0369a1; line-height: 1.4; }

        .confirm-actions { padding: 14px 18px; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
        .btn-confirm-cancel { background: #ffffff; border: 1px solid #cbd5e1; color: #334155; border-radius: 8px; padding: 7px 14px; font-size: 0.78rem; font-weight: 700; cursor: pointer; }
        .btn-confirm-cancel:hover { background: #f1f5f9; }
        .btn-confirm-action { background: #0071e3; color: #ffffff; border: none; border-radius: 8px; padding: 7px 16px; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; }
        .btn-confirm-action:hover:not(:disabled) { background: #0058b0; }

        /* TABLET & DESKTOP SCREEN ADJUSTMENTS */
        @media (min-width: 576px) {
          .precise-app-bg { padding: 2rem 1rem; }
          .precise-header { padding: 1.5rem 2rem; }
          .precise-header h1 { font-size: 1.3rem; }
          .receipt-brand-row { flex-direction: row; align-items: flex-start; }
          .receipt-meta-grid { grid-template-columns: 1fr 1fr; }
          .precise-footer { flex-direction: row; justify-content: space-between; text-align: left; }
          .mnc-receipt-body { padding: 24px; }
          .mnc-receipt-header { padding: 16px 22px; }
          .mnc-receipt-actions { padding: 16px 24px; }
          .confirm-header-content { padding: 24px 24px 16px; }
          .confirm-details-card { margin: 20px 24px 0; }
          .confirm-notice-box { margin: 16px 24px 0; }
          .confirm-actions { padding: 16px 24px; margin-top: 20px; }
        }

        @media print {
          .precise-app-bg { padding: 0; background: #fff; }
          .precise-toolbar, .precise-footer, .mnc-receipt-actions, .confirm-actions, .btn-view-receipt, .btn-generate-receipt { display: none !important; }
          .precise-card { box-shadow: none; border: none; }
        }
      `}</style>
    </div>
  );
};

export default PaidFees;